import { type FC, type PropsWithChildren, useEffect, useMemo, useState, createContext } from 'react'
import type { TileLayerOptions, WMSOptions } from 'leaflet'
import { fetchRaw } from '../../utils/api/api'
import joinUrl from '../../utils/joinUrl'
import environment from '../../../environment'
import buildLeafletLayers from '../../../pages/MapPage/utils/buildLeafletLayers'
import type { MapCollection } from '../../../types/mapTypes'
import { useIsEmbedded } from '../ui'
import type { ExtendedMapGroup } from '../../utils/map'
import { useMapContext } from '../map/MapContext'
import useParam from '../../hooks/useParam'
import { mapLayersParam } from '../../../pages/MapPage/query-params'

export interface WmsOverlay {
  type: 'wms'
  id: string
  url: string
  options: WMSOptions
  layer: ExtendedMapGroup
  params?: {
    [key: string]: string
  }
}

export interface TmsOverlay {
  type: 'tms'
  id: string
  url: string
  options: TileLayerOptions
  layer: ExtendedMapGroup
  params?: {
    [key: string]: string
  }
}

export type Overlay = WmsOverlay | TmsOverlay

interface MapPanelOverlay {
  id: string
  isVisible: boolean
}

export interface LayerState {
  panelLayers: MapCollection[]
  legendLeafletLayers: Overlay[]
}

export const LayerContext = createContext<LayerState | null>(null)

const getMaplayers = () => fetchRaw(joinUrl([environment.DATAPORTAAL_API_ROOT, 'maplayers']))

const LayerProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mapLayers, setMapLayers] = useState<MapCollection[]>([])
  const isEmbedView = useIsEmbedded()
  const [activeMapLayers] = useParam(mapLayersParam)
  const { activeEmbeddedMapLayers } = useMapContext()

  const overlays = useMemo<MapPanelOverlay[]>(() => {
    if (isEmbedView) {
      return activeMapLayers.map((activeMapLayer) => ({
        id: activeMapLayer,
        isVisible: activeEmbeddedMapLayers.find((layer) => layer === activeMapLayer) !== undefined,
      }))
    }

    return activeMapLayers.map((layer) => ({ id: layer, isVisible: true }))
  }, [activeMapLayers, activeEmbeddedMapLayers])
  // Todo: this should be refactored so we handle visibility of layers in components
  const formattedMapLayers = useMemo(
    () =>
      mapLayers.map((panelLayer) => ({
        ...panelLayer,
        mapLayers: panelLayer.mapLayers.map((mapLayer) => ({
          ...mapLayer,
          legendItems: [
            ...(mapLayer?.legendItems?.map((legendItem) => ({
              ...legendItem,
              authScope: mapLayer.authScope ?? null,
              url: legendItem?.url ?? mapLayer.url,
              isVisible: overlays.some(
                (overlay) =>
                  (overlay.id === legendItem.id && overlay.isVisible) || legendItem.notSelectable,
              ),
            })) || []),
          ],
          isVisible: overlays.some((overlay) =>
            [{ id: mapLayer.id }, ...(mapLayer.legendItems || [])].some(
              (legendItem) => overlay.id === legendItem.id && overlay.isVisible,
            ),
          ),
          isEmbedded: overlays.some((overlay) =>
            [{ id: mapLayer.id }, ...(mapLayer.legendItems || [])].some(
              (legendItem) => overlay.id === legendItem.id && isEmbedView,
            ),
          ),
        })),
      })),
    [overlays, isEmbedView, mapLayers],
  )

  const legendLeafletLayers = useMemo(() => {
    return buildLeafletLayers(
      isEmbedView ? activeEmbeddedMapLayers : activeMapLayers,
      // @ts-ignore
      formattedMapLayers
        .map((collection) =>
          collection.mapLayers.map((mapLayer) => [mapLayer, ...mapLayer.legendItems]),
        )
        .flat()
        .flat(),
    )
  }, [activeMapLayers, activeEmbeddedMapLayers, mapLayers])

  useEffect(() => {
    getMaplayers()
      .then((result): void => {
        setMapLayers(result ?? [])
      })
      .catch(() => console.error('MapContainer: problem fetching panel and map layers'))
  }, [])

  return (
    <LayerContext.Provider
      value={{
        panelLayers: formattedMapLayers,
        legendLeafletLayers,
      }}
    >
      {children}
    </LayerContext.Provider>
  )
}

export default LayerProvider
